import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from './services/auth.service';
import { UserService } from './services/user.service';
import { environment } from 'src/environments/environment';
import {
  ACTIVITY_LOG,
  BUDGET,
  PREVIEW,
  QUOTES,
  USER_CAN_DEPOSIT,
  USERS,
} from './services/constants';
import { UtilsService } from './services/utils.service';

@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate {
  userDetails: any;
  isAllowed: string | null;
  demoView: boolean = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private utilsService: UtilsService,
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const routePath = route.routeConfig?.path;

    // console.log('RoleGuard routePath', routePath);
    this.authService.redirectURL = routePath;
    const verified = await this.authService.checkIfUserVerified();
    if (verified) {
      try {
        this.userDetails = await this.authService.setAndGetCurrentUser();

        if (this.userDetails?.verified) {
          if (
            routePath === 'collections' &&
            ((this.userDetails?.permission_based &&
              !this.hasPermission(this.userDetails, 5)) ||
              !this.userDetails?.user_enabled)
          ) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (
            routePath === 'cart' &&
            ((this.userDetails?.permission_based &&
              !this.hasPermission(this.userDetails, 3)) ||
              !this.userDetails?.user_enabled)
          ) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (
            routePath === 'orders' &&
            ((this.userDetails?.permission_based &&
              !this.hasPermission(this.userDetails, 3) &&
              !this.hasPermission(this.userDetails, 6)) ||
              !this.userDetails?.user_enabled)
          ) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (
            routePath === 'users' &&
            (this.userDetails?.userType != 'Super user' ||
              !this.userDetails?.user_enabled ||
              !this.utilsService.hasFeatureCheck(
                this.userDetails?.planDetails,
                USERS,
              ))
          ) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (
            routePath === 'sub-accounts' &&
            (this.userDetails?.userType != 'Super user' ||
              !this.userDetails?.user_enabled ||
              this.userDetails?.user_plan !== 4 ||
              this.userDetails.isSubAccount)
          ) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (
            routePath === 'activity-log' &&
            (this.userDetails?.userType != 'Super user' ||
              !this.userDetails?.user_enabled ||
              this.userDetails?.user_plan !== 4 ||
              !this.utilsService.hasFeatureCheck(this.userDetails?.planDetails, ACTIVITY_LOG))
          ) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (
            routePath === 'quotes' &&
            (this.userDetails?.userType != 'Super user' ||
              !this.userDetails?.user_enabled ||
              this.userDetails?.allocated ||
              !this.utilsService.hasFeatureCheck(
                this.userDetails?.planDetails,
                QUOTES,
              ))
          ) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (
            routePath === 'budget' &&
            (!this.userDetails?.user_enabled ||
              this.userDetails?.allocated ||
              !this.utilsService.hasFeatureCheck(
                this.userDetails?.planDetails,
                BUDGET,
              ))
          ) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (
            routePath === 'preview' &&
            (!this.userDetails?.user_enabled ||
              this.userDetails?.allocated ||
              !this.utilsService.hasFeatureCheck(
                this.userDetails?.planDetails,
                PREVIEW,
              ))
          ) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (
            routePath === 'deposits' &&
            ((this.userDetails?.userType != 'Super user' ||
              !this.userDetails?.user_enabled ||
              this.userDetails?.allocated) &&
            this.userDetails?.permission_based &&
            !this.hasPermission(this.userDetails, USER_CAN_DEPOSIT)||
            this.userDetails?.isSubAccount) 
          ) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (routePath === 'whats-new' && !this.userDetails?.user_enabled) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (routePath === 'newbudget' && !this.userDetails?.user_enabled) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (
            routePath === 'newtask' &&
            ((this.userDetails?.permission_based &&
              !this.hasPermission(this.userDetails, 1)) ||
              !this.userDetails?.user_enabled)
          ) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }

          if (
            routePath === 'retask' &&
            ((this.userDetails?.permission_based &&
              !this.hasPermission(this.userDetails, 1)) ||
              !this.userDetails?.user_enabled)
          ) {
            this.router.navigate(['/task']); // redirect to /task if permission denied
            return false;
          }
        } else {
          // If user is not verified, logout and redirect to login
          this.authService.logoutAndReset();
          return false;
        }

        // console.log('RoleGuard canActivate', true);
        return true; // allow navigation
      } catch (error) {
        return false;
      }
    }

    // If not verified, logout and redirect to login
    this.authService.logoutAndReset();
    return false;
  }

  private hasPermission(user: any, requiredPermission: number): boolean {
    return user?.permissions.includes(requiredPermission);
  }
}
